/**
 * Alert Actions vocabulary shared by the site page (local node, "Alert Actions") and the
 * cloud page ("Central Alert Actions"), and by both Alert Rules forms.
 *
 * Per rule (command-center alert_configs):
 *   Dashboard    websocket_enabled        — live alert on the boards (default ON)
 *   Email        email_enabled + email_recipients
 *   Message API  message_api_enabled + message_api_recipients
 *   Sent by      notify_via 'local' (this site's channels) | 'cloud' (Central Alert Actions)
 *
 * Channels (where email / messages go out from): 'email' = SMTP server,
 * 'message_api' = Message API Server. One set per site, one set on the cloud.
 */

export type AlertChannelKey = 'email' | 'message_api';
export type NotifyVia = 'local' | 'cloud';

export interface AlertChannelState {
  channel: AlertChannelKey;
  enabled: boolean;
  configured: boolean;
  settings: Record<string, any>;
  has_secret: boolean;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_message: string | null;
  updated_at: string | null;
  /** Cloud email channel only: the Gmail API sender's connection. */
  gmail?: { has_client_secret: boolean; connected: boolean; account: string | null };
}

export interface SaveChannelBody {
  enabled?: boolean;
  settings?: Record<string, any>;
  /** Non-empty replaces the stored secret; empty/undefined keeps it. */
  secret?: string;
  clear_secret?: boolean;
  /** Gmail API sender (cloud): non-empty replaces the OAuth client secret. */
  gmail_client_secret?: string;
  /** Gmail API sender (cloud): forget the connected Google account. */
  disconnect_gmail?: boolean;
}

export interface TestChannelBody {
  /** Comma-separated recipients; empty = only connect / authenticate. */
  to?: string;
  /** Unsaved form values to test instead of the stored ones. */
  settings?: Record<string, any>;
  secret?: string;
  gmail_client_secret?: string;
}

export interface AlertChannelsApi {
  saveChannel: (channel: AlertChannelKey, body: SaveChannelBody) => Promise<AlertChannelState>;
  testChannel: (channel: AlertChannelKey, body: TestChannelBody) => Promise<{ ok: boolean; message: string }>;
  /**
   * Present only where the Gmail API sender exists (the cloud): start "Connect Google account"
   * for the saved Client ID/secret; resolves with Google's consent URL.
   */
  gmailConnect?: (redirectUri: string) => Promise<{ auth_url: string }>;
}

/** The cloud backend's Gmail OAuth callback — what goes under "Authorized redirect URIs". */
export const GMAIL_CALLBACK_PATH = '/api/alert-actions/gmail/callback';

/** The rule fields Alert Actions reads and writes. */
export interface RuleNotificationValue {
  websocket_enabled?: boolean | null;
  email_enabled?: boolean | null;
  email_recipients?: string | null;
  message_api_enabled?: boolean | null;
  message_api_recipients?: string | null;
  notify_via?: NotifyVia | string | null;
}

/** A new rule: Dashboard only. */
export const DEFAULT_RULE_NOTIFICATIONS: {
  websocket_enabled: boolean;
  email_enabled: boolean;
  email_recipients: string;
  message_api_enabled: boolean;
  message_api_recipients: string;
  notify_via: NotifyVia;
} = {
  websocket_enabled: true,
  email_enabled: false,
  email_recipients: '',
  message_api_enabled: false,
  message_api_recipients: '',
  notify_via: 'local',
};

export const CHANNEL_TITLE: Record<AlertChannelKey, string> = {
  email: 'Email (SMTP)',
  message_api: 'Message API Server',
};

/**
 * "Sent by" labels, from where the reader stands. On a site, 'local' is "This site";
 * on the cloud page (editing a site's rule) it is "The site".
 */
export function viaLabel(via: unknown, perspective: 'site' | 'central' = 'site'): string {
  if (via === 'cloud') return perspective === 'site' ? 'Via Cloud' : 'Cloud (central)';
  return perspective === 'site' ? 'This site' : 'The site';
}

export const isDashboardOn = (r: RuleNotificationValue) => r.websocket_enabled !== false;

export function splitRecipients(value: unknown): string[] {
  return [...new Set(String(value || '').split(/[,;\n]/).map((v) => v.trim()).filter(Boolean))];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_OR_GROUP_RE = /^(\+|00)?[\d\s\-()]{10,20}$|^\d{6,30}(-\d+)?(@g\.us)?$/i;

/** What stops these notification settings being saved, in the operator's terms ('' = fine). */
export function ruleNotificationProblem(r: RuleNotificationValue): string {
  if (r.email_enabled) {
    const to = splitRecipients(r.email_recipients);
    if (to.length === 0) return 'Add at least one email recipient, or turn Email off.';
    const bad = to.filter((t) => !EMAIL_RE.test(t));
    if (bad.length) return `Not an email address: ${bad.join(', ')}`;
  }
  if (r.message_api_enabled) {
    const to = splitRecipients(r.message_api_recipients);
    if (to.length === 0) return 'Add at least one number or WhatsApp group, or turn Message API off.';
    const bad = to.filter((t) => !PHONE_OR_GROUP_RE.test(t));
    if (bad.length) return `Not a phone number or group id: ${bad.join(', ')}`;
  }
  if (r.notify_via && r.notify_via !== 'local' && r.notify_via !== 'cloud') return 'Pick who sends the notifications.';
  return '';
}

/** Just the Alert Actions fields of a rule, with defaults filled in — the PATCH body. */
export function pickRuleNotifications(r: RuleNotificationValue) {
  return {
    websocket_enabled: isDashboardOn(r),
    email_enabled: !!r.email_enabled,
    email_recipients: splitRecipients(r.email_recipients).join(', '),
    message_api_enabled: !!r.message_api_enabled,
    message_api_recipients: splitRecipients(r.message_api_recipients).join(', '),
    notify_via: (r.notify_via === 'cloud' ? 'cloud' : 'local') as NotifyVia,
  };
}

/** Error text out of whatever shape an axios failure arrived in. */
export function alertActionsErrorText(err: any, fallback: string): string {
  return err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback;
}
